<template>
    <div class="flex text-blue-600 draggable" :style="'background-color: '+item.color" @mouseover="highlight"
         @mouseleave="unhighlight">
        <!-- Description. -->
        <div class="flex-1 flex m-0 -ml-px relative">
            <div
                class="flex flex-1 flex-row m-0 -ml-px -mt-px mr-px -mb-px border border-dashed border-gray-200 shadow-none"
                :style="'background-color: '+item.color"
                :class="estimate.readonly?'':'hover:border-gray-400 hover:z-10'">
                <!-- Draggable handle. -->
                <div class="w-6 flex-shrink-0 self-start pt-2 pl-1 -my-px">
                    <div class="w-5 h-5 flex items-center">
                        <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move pl-1 handle"
                           v-if="!estimate.readonly"></i>
                    </div>
                </div>
                <div class="flex-1" :key="renderKey">
                    <template v-if="estimate.readonly">
                        <div
                            class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative whitespace-pre-wrap focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            v-text="item.description"
                        ></div>
                    </template>
                    <estimate-task-item-existing-search
                        class="flex-1 flex"
                        #default="{ close, open, query, setQuery }"
                        :estimate="estimate"
                        :item="item"
                        :parentItem="parentItem"
                        :estimateClipboard="estimateClipboard"
                        @selected:existingItem="existingItemSelected"
                        :top="true"
                        v-else
                    >
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-description"
                        >
                            <textarea
                                placeholder="Description..."
                                class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                                :style="'background-color: '+item.color+';border-color: '+item.color"
                                type="text"
                                ref="description"
                                :disabled="estimate.readonly"
                                v-model="item.description"
                                @input="setQuery($event.target.value);estimate.update()"
                                @focus="setQuery($event.target.value);open();searcherOpen = true;"
                                @blur="estimate.buildCollectedItems();searcherOpen = false;"
                                @change="estimate.addSnapshot()"

                                rows="1"
                            ></textarea>
                        </autoresizable-textarea>
                    </estimate-task-item-existing-search>
                    <estimate-assembly-pinger :item="item" :estimate="estimate" v-if="showPinger()"
                                              :pinging="isPinging()" class="top-2 right-2"></estimate-assembly-pinger>
                    <div
                        v-if="item.discount && estimate.readonly"
                        class="text-sm text-gray-400 m-0 px-2 -mt-2 pt-1.5 pb-1.5 whitespace-pre-wrap shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                        :style="'background-color: '+item.color+';border-color: '+item.color"
                        v-text="item.discountComment?item.discountComment:' '"
                    ></div>
                    <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                            ref="autoresizable-discount-comment"
                                            :enabled="inView"
                                            v-else-if="(item.discount && !estimate.readonly) || editing==='discount' || editing==='discountRate'">
                        <textarea
                            placeholder="Discount explanation..."
                            class="text-sm text-gray-400 m-0 px-2 -mt-2 pt-1.5 pb-1.5 placeholder:text-gray-400 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            type="text"
                            ref="description"
                            :disabled="estimate.readonly"
                            v-model="item.discountComment"
                            @input="estimate.update()"
                            @change="estimate.addSnapshot()"
                            rows="1"
                        ></textarea>
                    </autoresizable-textarea>
                </div>
            </div>
        </div>

        <!-- Quantity. -->
        <div class="w-20 m-0 -ml-px flex">
            <page-cell
                class="text-sm -m-px px-2 py-1.5 border border-dashed border-gray-200"
                :style="'background-color: '+item.color"
                placeholder="Qty"
                :read-only="estimate.readonly"
                v-model="quantity"
                @input="updateQuantity"
                @blur="updateAll()"
                @change="updateAll();estimate.addSnapshot()"
            ></page-cell>
        </div>

        <!-- Units of measure. -->
        <div class="w-20 m-0 -ml-px flex">
            <page-cell
                class="text-sm -m-px px-2 py-1.5 border border-dashed border-gray-200"
                :style="'background-color: '+item.color"
                placeholder="Uom"
                :read-only="estimate.readonly"
                v-model="item.uom"
                @input="estimate.update()"
                @change="estimate.addSnapshot()"
            ></page-cell>
        </div>

        <!-- Rate. -->
        <div class="w-28 m-0 -ml-px flex" v-if="item.quantity">
            <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full" :enabled="inView"
                         trigger-class="flex w-full">
                <template #default="{ open, close }">
                    <div
                        class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border border-dashed border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            currency
                            class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :placeholder="item.quantity?(item.total/item.quantity):0 | toCurrency()"
                            :read-only="estimate.readonly"
                            v-model="rate"
                            @input="updateRate"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                            @mouseenter="open"
                            @mouseleave="close"
                        ></page-cell>
                        <page-cell
                            v-if="item.discountPercent || editing==='discountRate' || editing==='discount'"
                            currency
                            class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :placeholder="item.quantity?(item.discount/item.quantity):0  | toCurrency()"
                            :read-only="estimate.readonly"
                            v-model="discountRate"
                            @input="updateDiscountRate"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                            @mouseenter="open"
                            @mouseleave="close"
                        ></page-cell>
                    </div>
                </template>
                <template #menu>
                    <table class="w-full">
                        <tbody>
                        <tr>
                            <td class="text-gray-600 text-sm" v-if="item.uom">Cost rate per {{ item.uom }}</td>
                            <td class="text-gray-600 text-sm" v-else>Cost Rate</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold text-red-400"
                                v-if="item.markupFixedValue">
                                * {{
                                    (item.buildTotalMarkedUp - item.profit - item.overheads) / item.quantity | toCurrency
                                }}
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                v-else>
                                {{ item.buildTotal / item.quantity | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Overheads <span
                                class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ (item.overheads / item.quantity) | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Profit <span
                                class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ (item.profit / item.quantity) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr v-if="item.discountPercent">
                            <td class="text-gray-600 text-sm">Less discount <span
                                class="text-gray-400">({{ round(item.discountPercent, 2 %)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.discount / item.quantity) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.adjustment">
                            <td class="text-gray-600 text-sm">Less adjustment <span
                                class="text-gray-400">({{ round(estimate.adjustment, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.adjustment / item.quantity) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.mcd">
                            <td class="text-gray-600 text-sm">Less MCD <span
                                class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.mcd / item.quantity) | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Total</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ item.finalTotal / item.quantity | toCurrency }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </template>
            </ui-dropdown>
        </div>
        <div class="w-28 m-0 -ml-px flex" v-else>
            <page-cell
                class="text-sm text-right -m-px px-2 py-1.5 border border-dashed border-gray-200 text-red-400"
                :style="'background-color: '+item.color"
                disabled=""
                value="???"
            ></page-cell>
        </div>

        <!-- Total. -->
        <div class="w-28 m-0 -ml-px flex">
            <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full" :enabled="inView"
                         trigger-class="flex w-full">
                <template #default="{ open, close }">
                    <div
                        class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border border-dashed border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            currency
                            class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :placeholder="item.valueWithDefaultOHP | toCurrency()"
                            :read-only="estimate.readonly"
                            v-model="total"
                            @input="updateTotal"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                            @mouseenter="open"
                            @mouseleave="close"
                        ></page-cell>
                        <page-cell
                            v-if="item.discountPercent || editing==='discount' || editing==='discountRate'"
                            currency
                            class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-1.5 focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :placeholder="item.discount | toCurrency()"
                            :read-only="estimate.readonly"
                            v-model="discount"
                            @input="updateDiscount"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                            @mouseenter="open"
                            @mouseleave="close"
                        ></page-cell>
                        <div class="absolute top-2 left-1 z-20" v-if="item.markupFixedValue">
                            <i class="fas fa-star text-blue-600" style="font-size: 8px"></i>
                        </div>
                        <div class="absolute top-2 left-1 z-20"
                             v-else-if="(round(item.buildTotal*(1+(estimate.profit+estimate.overheads)/100),2) > item.buildTotalMarkedUp)">
                            <i class="fas fa-arrow-down text-red-600" style="font-size: 9px"></i>
                        </div>
                        <div class="absolute top-2 left-1 z-20"
                             v-else-if="(round(item.buildTotal*(1+(estimate.profit+estimate.overheads)/100),2) < item.buildTotalMarkedUp)">
                            <i class="fas fa-arrow-up text-green-600" style="font-size: 9px"></i>
                        </div>

                    </div>
                </template>
                <template #menu>
                    <table class="w-full">
                        <tbody>
                        <tr>
                            <td class="text-gray-600 text-sm">Material Cost</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                <div
                                    v-if="item.markupFixedValue"
                                    class="text-red-400">
                                    ???
                                </div>
                                <div v-else>
                                    {{ item.materialBuildTotal | toCurrency }}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Labour Cost</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                <div
                                    v-if="item.markupFixedValue"
                                    class="text-red-400">
                                    ???
                                </div>
                                <div v-else>
                                    {{ item.labourBuildTotal | toCurrency }}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Fixed Cost</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                <div
                                    v-if="item.markupFixedValue"
                                    class="text-red-400">
                                    ???
                                </div>
                                <div v-else>
                                    {{ item.fixedCostBuildTotal | toCurrency }}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm font-semibold">Total Cost</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                <div
                                    v-if="item.markupFixedValue"
                                    class="text-red-400">
                                    * {{ item.buildTotalMarkedUp - item.profit - item.overheads | toCurrency }}
                                </div>
                                <div v-else>
                                    {{ item.buildTotal | toCurrency }}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Overheads <span
                                class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ (item.overheads) | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Profit <span
                                class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ (item.profit) | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm font-semibold">Sub Total</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ (item.buildTotalMarkedUp) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr v-if="item.discountPercent">
                            <td class="text-gray-600 text-sm">Less discount <span
                                class="text-gray-400">({{ round(item.discountPercent, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.discount) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.adjustment">
                            <td class="text-gray-600 text-sm">Less adjustment <span
                                class="text-gray-400">({{ round(estimate.adjustment, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.adjustment) | toCurrency }}
                            </td>
                        </tr>
                        <tr v-if="item.mcd">
                            <td class="text-gray-600 text-sm">Less MCD <span
                                class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ 0 - (item.mcd) | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                <div class="border-b border-dashed border-gray-200 my-1"></div>
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm font-semibold">Grand Total</td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ item.finalTotal | toCurrency }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-gray-600 text-sm">Effective OHP <span
                                class="text-gray-400">({{ round(item.effectiveOHPPercent, 2) }}%)</span>
                            </td>
                            <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                {{ item.effectiveOHP | toCurrency }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </template>
            </ui-dropdown>
        </div>

        <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t border-dashed pl-1 pr-1">
            <button @click="$modals.open('estimate-task-item-build', { item, estimate, store })"
                    class="text-gray-300 text-xs hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded mt-0.5">
                <i class="fas fa-hammer"></i>
            </button>
        </div>
        <!-- Dropdown. -->
        <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t border-dashed pl-1 pr-1">
            <ui-dropdown menu-class="p-3 w-56 z-50" :close-on-mouse-out-scroll="true" :enabled="inView">
                <template #default="{ toggle }">
                    <button @click="toggle"
                            :style="'background-color: '+item.color"
                            class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                        <i class="fa fa-ellipsis-h"></i>
                    </button>
                </template>
                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-calculator"
                                      @mousedown="$modals.open('estimate-item-cost-list', { item, task, section, estimate })">
                        See Cost Breakdown
                    </ui-dropdown-item>
                    <ui-dropdown-divider></ui-dropdown-divider>
                    <ui-dropdown-item icon="fas fa-cut" @mousedown="cutItem();close();" v-if="!estimate.readonly">Cut
                        Item
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-copy" @mousedown="copyItem();close();">Copy Item
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-paste"
                                      v-if="!estimate.readonly && (estimateClipboard[0])"
                                      @mousedown="pasteItem(estimateClipboard[0],item);close();">
                        Paste Item
                    </ui-dropdown-item>
                    <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                    <ui-dropdown-item icon="fas fa-hammer"
                                      v-if="!estimate.readonly"
                                      @mousedown="editAssembly();close();">
                        Edit Assembly
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="far fa-window-restore"
                                      v-if="!estimate.readonly"
                                      @mousedown="findAndReplace(close)">
                        Find &amp; Replace
                    </ui-dropdown-item>
                    <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                    <ui-dropdown-item ref="paste1item"
                                      @mouseleave="closePasteSubMenu(1)"
                                      @mouseenter="openPasteSubMenu(1)"
                                      v-if="!estimate.readonly"
                                      icon="fas fa-clipboard"
                                      :submenu="true"
                    >Library
                        <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                             :ref="'paste1relative'">
                            <vue-context :ref="'paste1menu'" :autoFocus="false"
                                         class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                         :close-on-click="false" :close-on-scroll="false">
                                <div :id="'paste1menu'"
                                     class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                    <ui-dropdown-item icon="fas fa-plus"
                                                      @mousedown="addToLibrary(item);close();">
                                        Add Item to Library
                                    </ui-dropdown-item>
                                    <ui-dropdown-divider
                                        v-if="!estimate.readonly && (estimateClipboard.length > 1)"></ui-dropdown-divider>
                                    <div class="max-h-96 overflow-x-auto">
                                        <ui-dropdown-item :ref="'paste999'+groupIndex+'item'"
                                                          v-for="(group,groupIndex) in groupedEstimateClipboard()"
                                                          :key="'paste999'+groupIndex"
                                                          v-if="group.name"
                                                          @mouseleave="closePasteSubMenu('999'+groupIndex)"
                                                          @mouseenter="openPasteSubMenu('999'+groupIndex)"
                                                          :submenu="true"
                                                          icon=" "
                                        >{{ group.name }}
                                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                                 :ref="'paste999'+groupIndex+'relative'">
                                                <vue-context :ref="'paste999'+groupIndex+'menu'" :autoFocus="false"
                                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                             :close-on-click="false" :close-on-scroll="false">
                                                    <div :id="'paste999'+groupIndex+'menu'"
                                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                                        <ui-dropdown-item icon="fas fa-paste"
                                                                          button-class="truncate"
                                                                          class="w-full truncate flex"
                                                                          v-for="(tem,i) in group.items"
                                                                          :key="'paste999'+groupIndex+'-'+i"
                                                                          @mousedown="pasteItem(tem.data,item);close();">
                                                            <div class="text-xs text-blue-500 truncate">{{
                                                                    tem.name
                                                                }}
                                                            </div>
                                                        </ui-dropdown-item>
                                                    </div>
                                                </vue-context>
                                            </div>
                                        </ui-dropdown-item>
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(tem,i) in groupedEstimateClipboard().find((a) => a.name === '').items"
                                                          :key="'paste1-'+i"
                                                          @mousedown="pasteItem(tem.data,item);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ tem.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </div>
                            </vue-context>
                        </div>
                    </ui-dropdown-item>
                    <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                    <ui-dropdown-item v-if="!estimate.readonly" icon="fas fa-trash" danger
                                      @mousedown="deleteItem();close();">Delete
                    </ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>
    </div>
</template>

<script>

import UiDropdownItem from "../../ui/UiDropdownItem.vue"
import {cloneDeep} from "lodash"
import {resolveNumericText, toCurrency} from "../../../utils"
import AutoresizableTextarea from "../../ui/AutoresizableTextarea"
import remoteStorage from "../../../utils/remoteStorage"
import {Estimate} from "@models"
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue"
import EstimateAssemblyPinger from "./EstimateAssemblyPinger.vue";
import EstimateTaskItemExistingSearch from "./EstimateTaskItemExistingSearch.vue";
import * as math from 'mathjs';

export default {
    components: {
        EstimateTaskItemExistingSearch,
        EstimateAssemblyPinger, UiDropdownDivider, AutoresizableTextarea, UiDropdownItem
    },
    props: {
        estimate: Object,
        section: Object,
        task: Object,
        item: Object,
        parentItem: Object,
        store: Object,
        pinging: null,
        inView: true,
        estimateClipboard: Array
    },
    data() {
        return {
            quantity: this.item.quantity ? this.item.quantity : '',
            total: this.item.buildTotalMarkedUp,
            discount: toCurrency(0 - Math.abs(this.item.discount)),
            adjustment: toCurrency(0 - Math.abs(this.item.adjustment)),
            mcd: toCurrency(0 - Math.abs(this.item.mcd)),
            rate: this.item.buildTotalMarkedUp / this.item.quantity,
            discountRate: toCurrency(0 - (Math.abs(this.item.discount) / this.item.quantity)),
            adjustmentRate: toCurrency(0 - (Math.abs(this.item.adjustment) / this.item.quantity)),
            mcdRate: toCurrency(0 - (Math.abs(this.item.mcd) / this.item.quantity)),
            editing: false,
            renderKey: 0,
            resolveNumericText,
//            estimateClipboard: estimateClipboardData,
            searcherOpen: false,
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (this.editing !== 'quantity') {
                    this.quantity = this.item.quantity ? this.item.quantity : ''
                }
                if (this.editing !== 'total') {
                    this.total = this.item.buildTotalMarkedUp
                }
                if (this.editing !== 'rate') {
                    this.rate = math.round((this.item.quantity ? (this.item.buildTotalMarkedUp / this.item.quantity) : 0), 2)
                }
                if (this.editing !== 'discount') {
                    this.discount = toCurrency(0 - Math.abs(this.item.discount))
                }
                if (this.editing !== 'discountRate') {
                    this.discountRate = toCurrency(this.item.quantity ? (0 - Math.abs(this.item.discount / this.item.quantity)) : 0)
                }
                this.mcd = toCurrency(0 - Math.abs(this.item.mcd))
                this.adjustmentRate = toCurrency(0 - (Math.abs(this.item.adjustment) / this.item.quantity))
                this.adjustment = toCurrency(0 - (Math.abs(this.item.adjustment)))
                this.mcdRate = toCurrency(0 - (Math.abs(this.item.mcd) / this.item.quantity))
                this.mcd = toCurrency(0 - (Math.abs(this.item.mcd)))
            },
            deep: true,
        },
        "estimate.collectionBuildUUID": {
            handler(newValue, oldValue) {
                this.renderKey = this.renderKey + 1
            },
            deep: true,
        }
    },
    methods: {
        existingItemSelected(item) {
            if (Estimate.itemAsKey(this.item) !== Estimate.itemAsKey(item.item)) {
                let q = this.item.quantity
                let ref = this.item.reference
                Object.assign(this.item, cloneDeep(item.item))
                this.item.reference = ref
                // clear any discounts
                this.item.discountPercent = 0
                this.item.discount = 0
                this.item.discountComment = ''
                this.quantity = q
                this.updateQuantity()
                this.estimate.addSnapshot()
            }
        },
        showPinger() {
            let m = this.estimate.collectedItems.items.get(Estimate.itemAsKey(this.item))
            return !this.item.bundle && (m && (m.quantity > 1))
        },
        isPinging() {
            return this.pinging === Estimate.itemAsKey(this.item)
        },
        highlight() {
            this.$emit('ping', Estimate.itemAsKey(this.item))
        },
        unhighlight() {
            this.$emit('ping', null)
        },
        groupedEstimateClipboard() {
            let ret = [{name: '', items: []}];
            this.estimateClipboard.forEach((i, index) => {
                if (index > 0) {
                    let groupName = ''
                    if (i.group) {
                        groupName = i.group
                    }
                    let group = ret.find((a) => a.name === groupName)
                    if (!group) {
                        group = {name: groupName, items: []}
                        ret.push(group)
                    }
                    group.items.push(i)
                }
            })
            return ret
        },
        addToLibrary(item) {
            this.$modals.open(
                'estimate-add-item-to-library',
                {
                    name: (item.description + ' - ' + item.reference + ' (' + toCurrency(item.total) + ')').replace(/(\r\n|\n|\r)/gm, ""),
                    title: `Add Item to Library`,
                    button: 'Add Item',
                    handle: (name, group) => {
                        this.clipboardItemData.push({
                            name: name,
                            group: group,
                            data: cloneDeep(item)
                        })
                        remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.clipboardItemData))
                        remoteStorage.setItem('estimate-clipboard-view', 'item')
                    }
                }
            )
        },
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        pasteItem(item, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'item')
            let num = this.parentItem.bundle.indexOf(at)
            if (item.bundle) {
                this.parentItem.bundle.splice(num, 0, ...cloneDeep(item.bundle))
            } else {
                this.parentItem.bundle.splice(num, 0, cloneDeep(item))
            }
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17)
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28)
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        isNumeric(num) {
            return (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num)
        },
        cutItem() {
            this.estimateClipboard[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.estimateClipboard))
            remoteStorage.setItem('estimate-clipboard-view', 'item')
            this.task.items = this.task.items.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        copyItem() {
            this.estimateClipboard[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.estimateClipboard))
            remoteStorage.setItem('estimate-clipboard-view', 'item')
        },
        deleteItem() {
            this.parentItem.bundle = this.parentItem.bundle.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        updateTotal(val) {
            this.editing = 'total'
            let toMatch = this.item.valueWithDefaultOHP
            let value
            if (val === '') {
                value = toMatch
            } else {
                value = math.round(this.resolveNumericText(val), 3)
            }
            let clearFixed = value === toMatch
            if (this.item.buildTotal) {
                this.item.markupFixedValue = null
                let markup = value - this.item.buildTotal
                this.item.profitPercent = clearFixed ? this.estimate.profit : (this.item.buildTotal ? (markup / this.item.buildTotal) * 100 : 0) - this.estimate.overheads
                this.item.fixedProfit = !clearFixed
            } else {
                this.item.markup = 0
                this.item.markupFixedValue = value
            }
            this.estimate.update()
        },
        updateDiscount(val) {
            this.editing = 'discount'
            this.item.discountPercent = Math.min(100, Math.max(0, (math.round(this.resolveNumericText(val, true), 2)) / this.item.buildTotalMarkedUp * 100))
            this.estimate.update()
            this.discountRate = toCurrency(0 - Math.abs(math.round((this.quantity ? (this.item.discount / this.quantity) : 0), 2)))
        },
        updateRate(val) {
            this.editing = 'rate'
            let clearFixed = false
            if (val === '') {
                val = this.item.buildTotal * (1 + (this.estimate.profit + this.estimate.overheads) / 100) / this.item.quantity
                clearFixed = true
            }
            let value = math.round(this.resolveNumericText(val), 3)
            if (this.item.buildTotal) {
                this.item.markupFixedValue = null
                let markup = (value * this.item.quantity) - this.item.buildTotal
                this.item.profitPercent = (this.item.buildTotal ? (markup / this.item.buildTotal) * 100 : 0) - this.estimate.overheads
                this.item.fixedProfit = !clearFixed
            } else {
                this.item.markup = 0
                this.item.markupFixedValue = value * this.item.quantity
            }
            this.estimate.update()
        },
        updateDiscountRate(val) {
            this.editing = 'discountRate'
            let value = math.round(this.resolveNumericText(val, true), 2)
            this.item.discountPercent = Math.min(100, Math.max(0, (value * this.item.quantity) / this.item.buildTotalMarkedUp * 100))
            this.estimate.update()
        },
        updateQuantity() {
            this.editing = 'quantity'
            let value = math.round(this.resolveNumericText(this.quantity), 3)
            this.estimate.setItemQuantity(this.item, isNaN(value) ? 0 : value)
            this.estimate.update()
        },
        updateAll(val) {
            this.editing = false
            this.quantity = this.item.quantity ? this.item.quantity : ''
            this.total = this.item.buildTotalMarkedUp
            this.rate = math.round((this.quantity ? (this.total / this.quantity) : 0), 2)
            this.discount = toCurrency(0 - Math.abs(this.item.discount))
            this.discountRate = toCurrency(0 - Math.abs(math.round((this.quantity ? (this.item.discount / this.quantity) : 0), 2)))
        },
        getItemColor() {
            return this.estimate.collectedItems.items.has(Estimate.itemAsKey(this.item)) ? this.estimate.collectedItems.items.get(Estimate.itemAsKey(this.item)).color : ''
        },
        editAssembly() {
            this.$modals.open('estimate-task-item-build', {
                item: this.item,
                editAsAssembly: true,
                estimate: this.estimate,
                store: this.store
            })
        },
        findAndReplace(close) {
            this.$modals.open('estimate-task-item-find-and-replace', {
                estimate: this.estimate,
                section: this.section,
                task: this.task,
                item: this.item,
                close: close,
                handle: function (data) {
                    data.items.forEach((item) => !data.partial ? item.description = data.replace : item.description = item.description.replaceAll(data.search, data.replace));
                    this.estimate.update()
                    this.estimate.addSnapshot()
                    this.$emit('reflow')
                }
            })
        },
    }
}
</script>
